/* global Excel */

const CUR_FMT = '#,##0.00 "₾"';

/**
 * Writes an auto-evaluation estimate to its own worksheet.
 *
 * items:   [{ name, code, unit, qty, unitTotal }]
 * summary: [{ label, pct }] applied one after another on the running total
 *          (e.g. unforeseen 5%, VAT 18%, extras...) — same order as the TOTAL sheet.
 */
export async function writeAutoEvaluateSheet({ title, projectName, items, summary }) {
  return Excel.run(async (context) => {
    const name = "ავტოშეფასება";
    let sheet = context.workbook.worksheets.getItemOrNullObject(name);
    await context.sync();
    if (sheet.isNullObject) {
      sheet = context.workbook.worksheets.add(name);
    } else {
      sheet.getUsedRange().clear(Excel.ClearApplyTo.all);
    }

    sheet.getRange("A:A").format.columnWidth = 18;
    sheet.getRange("B:B").format.columnWidth = 36;
    sheet.getRange("C:C").format.columnWidth = 300;
    sheet.getRange("D:D").format.columnWidth = 110;
    sheet.getRange("E:E").format.columnWidth = 60;
    sheet.getRange("F:F").format.columnWidth = 80;
    sheet.getRange("G:G").format.columnWidth = 100;
    sheet.getRange("H:H").format.columnWidth = 110;

    sheet.getRange("B2").values = [[`${title}${projectName ? ` — ${projectName}` : ""}`]];
    sheet.getRange("B2:H2").merge();
    const banner = sheet.getRange("B2:H2");
    banner.format.fill.color = "#00B3B3";
    banner.format.font.color = "white";
    banner.format.font.bold = true;
    banner.format.font.size = 12;
    sheet.getRange("2:2").format.rowHeight = 26;

    const head = [["N", "სამუშაო", "კოდი", "ერთ.", "რაოდ.", "ერთ. ფასი", "ჯამი"]];
    sheet.getRange("B4:H4").values = head;
    const headRange = sheet.getRange("B4:H4");
    headRange.format.fill.color = "#1B2A33";
    headRange.format.font.color = "white";
    headRange.format.font.bold = true;
    headRange.format.horizontalAlignment = "Center";

    const first = 5;
    const rows = items.map((it, i) => [i + 1, it.name, it.code, it.unit, it.qty, it.unitTotal, ""]);
    if (rows.length) {
      sheet.getRange(`B${first}:H${first + rows.length - 1}`).values = rows;
      for (let i = 0; i < rows.length; i += 1) {
        sheet.getRange(`H${first + i}`).formulas = [[`=F${first + i}*G${first + i}`]];
      }
      sheet.getRange(`G${first}:H${first + rows.length - 1}`).numberFormat =
        rows.map(() => [CUR_FMT, CUR_FMT]);
    }
    const last = first + Math.max(rows.length, 1) - 1;

    // Running total: sum, then each percentage on top of the previous sum.
    let r = last + 2;
    sheet.getRange(`D${r}`).values = [["ჯამი"]];
    sheet.getRange(`H${r}`).formulas = [[`=SUM(H${first}:H${last})`]];
    let prev = r;
    summary.forEach((s) => {
      r += 1;
      sheet.getRange(`D${r}`).values = [[`${s.label} ${s.pct}%`]];
      sheet.getRange(`H${r}`).formulas = [[`=H${prev}*${(Number(s.pct) || 0) / 100}`]];
      r += 1;
      sheet.getRange(`D${r}`).values = [["ჯამი"]];
      sheet.getRange(`H${r}`).formulas = [[`=H${prev}+H${r - 1}`]];
      prev = r;
    });
    const totalsStart = last + 2;
    sheet.getRange(`H${totalsStart}:H${r}`).numberFormat =
      Array.from({ length: r - totalsStart + 1 }, () => [CUR_FMT]);
    const totals = sheet.getRange(`D${totalsStart}:H${r}`);
    totals.format.font.bold = true;
    sheet.getRange(`D${r}:H${r}`).format.fill.color = "#00B3B3";
    sheet.getRange(`D${r}:H${r}`).format.font.color = "white";

    sheet.activate();
    await context.sync();
  });
}
